import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SectionCard } from '@/components/common/SectionCard';
import type { CreateStartupFormValues, FoundingTeamMemberFormValues } from '../../schema';
import { CREATE_STARTUP_STRINGS, MAX_FOUNDING_TEAM, SECTION_TITLES } from '../../constants';
import { CoFounderCard } from '../CoFounderCard';
import { FounderSelfIdentificationFields } from '../FounderSelfIdentificationFields';

interface StepProps {
  form: UseFormReturn<CreateStartupFormValues>;
}

/** Step 2 "Founding Team" (Figma 196:656). */
export function Step2FoundingTeam({ form }: StepProps) {
  const { control, watch, setValue } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'foundingTeam' });
  const categories = watch('founderUnderrepresentedCategories');

  return (
    <SectionCard title={SECTION_TITLES.FOUNDING_TEAM} subtitle={CREATE_STARTUP_STRINGS.TEAM_SUBTITLE}>
      <div className="flex flex-col gap-6">
        {fields.map((field, index) => (
          <CoFounderCard
            key={field.id}
            form={form}
            index={index}
            onRemove={index > 0 ? () => remove(index) : undefined}
          />
        ))}

        <FounderSelfIdentificationFields
          value={{
            founderFemale: watch('founderFemale'),
            founderUnderrepresented: watch('founderUnderrepresented'),
            founderUnderrepresentedCategories: Array.isArray(categories) ? categories : [],
          }}
          onChange={(next) => {
            const options = { shouldDirty: true } as const;
            setValue('founderFemale', next.founderFemale, options);
            setValue('founderUnderrepresented', next.founderUnderrepresented, options);
            setValue(
              'founderUnderrepresentedCategories',
              next.founderUnderrepresentedCategories,
              options,
            );
          }}
        />

        {fields.length < MAX_FOUNDING_TEAM && (
          <Button
            type="button"
            variant="dashed"
            className="inline-flex w-full items-center justify-center gap-2"
            onClick={() =>
              // pastExits intentionally omitted - optional Yes/No (2026-08-05)
              // with no sensible default to pre-fill, see EMPTY_MEMBER in
              // CreateStartupForm.tsx.
              append({
                name: '',
                title: '',
                linkedinUrl: '',
              } as FoundingTeamMemberFormValues)
            }
          >
            <Plus size={16} aria-hidden="true" />
            {CREATE_STARTUP_STRINGS.ADD_MEMBER}
          </Button>
        )}
      </div>
    </SectionCard>
  );
}
